/* Built In Imports */

/* External Imports */
import { Link } from '@chakra-ui/next-js';
import { Box, Heading, Stack, Tag, Text } from '@chakra-ui/react';

import { format } from 'date-fns';
/* Internal Imports */
/* Components */
import LazyLoadImageComponent from '@app-ps/Components/Ui/LazyLoadImageComponent';
import {
  isOpenInNewTab,
  refineUrlDomain,
} from '@app-ps/Components/Utility/utils';
import config from '@config';

/**
 * Renders WorkshopsCard
 * @param {object} workShopDetails - api Data
 * @returns {ReactElement} WorkshopsCard
 */
const WorkshopsCard = ({ workShopDetails, sectionTheme, ...props }) => {
  const {
    samskriti,
    amount,
    currency,
    title,
    date,
    time,
    address,
    popup_description,
    icon,
    button_label,
    is_online,
    program_id,
  } = workShopDetails;
  const categoryTags = samskriti.category_tags.split(',');
  const isProductions = categoryTags.indexOf('Production') > -1;
  const featuredImage = samskriti.featured_image.replace(/\\/g, '').split(',');
  const detailPageUrl = isProductions
    ? `productions/upcoming?id=${program_id}`
    : ``;
  const registerUrl = isProductions
    ? detailPageUrl
    : workShopDetails.samskriti?.register_url;
  // Format Date
  // const parts = date.split(' - ');
  // const startDay = parts[0].trim();
  // const endDay = parts[1].trim().split(' ')[0];
  // const month = parts[1].trim().split(' ')[1];
  // const formattedDate = `${startDay} ${month} - ${endDay} ${month}`;

  let displayDate = '';
  // Helper function to get day, month, year and time
  const formatDate = dateStr => {
    const date = new Date(dateStr);
    const day = format(date, 'd');
    const month = format(date, 'MMMM');
    const year = format(date, 'yyyy');
    const time = format(date, 'h:mm a'); // Format time as "5 PM" or "5 AM"
    return { day, month, year, time };
  };

  if (samskriti?.startdate) {
    const {
      day: startDay,
      month: startMonth,
      year: startYear,
      time: startTime,
    } = formatDate(samskriti?.startdate || '');
    const {
      day: endDay,
      month: endMonth,
      year: endYear,
    } = formatDate(samskriti?.enddate || '');

    // Check if start and end date are the same or different
    if (
      startDay === endDay &&
      startMonth === endMonth &&
      startYear === endYear
    ) {
      // Same start and end date
      displayDate = `${startDay} ${startMonth} ${startYear} | ${startTime}`;
    } else {
      // Different start and end dates
      displayDate = `${startDay} - ${endDay} ${startMonth} ${startYear} | ${startTime}`;
    }
  }

  return (
    <Box
      maxW={{ base: '280px', md: '322px' }}
      mx={'auto'}
      width="95% !important"
      className="workshop card"
      {...props}
    >
      <Box w="100%" h="100%">
        <Box
          // w="100%"
          position="relative"
          width={{ base: '100%', '2xl': '322px' }}
          height={{ '2xl': '293px' }}
        >
          <LazyLoadImageComponent
            minHeight={'255px'}
            background="lightgray"
            src={featuredImage[0]}
            alt={title}
            objectFit="cover"
            objectPosition="center"
            width={{ xl: '100%' }}
            height={{ xl: '100%' }}
          />
          <Box
            alignItems="center"
            backgroundColor="rgba(9, 13, 17, 0.8)"
            bottom="0"
            display="flex"
            h="2.5rem"
            justifyContent="left"
            left="0"
            maxW={{ base: '85%', md: '75%', '2xl': '51.5%' }}
            p="2"
            position="absolute"
            right="0"
            w="auto"
          >
            <LazyLoadImageComponent
              src={`${config.imgPath}/d/${
                is_online === '1'
                  ? '46272/1723466202-icon.png'
                  : '46272/1723466196-icon-1.png'
              }`}
              alt="location"
              w="14px"
            />
            <Text
              fontFamily="var(--FedraSansStd-book)"
              fontSize="0.875rem"
              fontWeight="400"
              color={'white'}
              pl={2}
              noOfLines={{ lg: '1' }}
            >
              {is_online === '1' ? samskriti?.popup_description : address}
            </Text>
          </Box>
        </Box>
        <Box spacing="3" w="100%" h="auto" position="relative">
          <Box
            h="auto"
            px={{ base: '1rem' }}
            backgroundColor="rgba(14, 18, 22, 1)"
            minH="267px"
          >
            <Box py={4}>
              <Stack direction="row" spacing={4} mb="4px">
                {categoryTags.map(tag => (
                  <Tag
                    _hover={{ background: 'transparent' }}
                    borderColor={'rgba(241, 237, 233, 0.2)'}
                    h="auto"
                    p="5px 10px"
                    variant="outline"
                  >
                    <span
                      style={{
                        fontWeight: '400',
                        fontFamily: 'var(--FedraSansStd-book)',
                        lineHeight: '1.4rem',
                        color: 'rgba(241, 237, 233, 1)',
                        borderColor: 'rgba(241, 237, 233, 0.2)',
                        fontSize: '0.75rem', // default for base
                        '@media(minWidth: 48em)': {
                          // from md breakpoint
                          fontSize: '0.875rem',
                        },
                      }}
                    >
                      {tag}
                    </span>
                  </Tag>
                ))}
              </Stack>
              <Heading
                w="100%"
                fontFamily="var(--FedraSansStd-medium)"
                fontSize={{ base: '1.125rem', lg: '1.25rem' }}
                fontWeight="500"
                lineHeight={{ base: '1.75rem', md: '2rem' }}
                color={'#FEFAF6'}
                textTransform="uppercase"
                mb="10px"
                letterSpacing="0.4px"
                noOfLines={2}
              >
                {isProductions ? (
                  <Link
                    href={refineUrlDomain(
                      isProductions
                        ? `productions/upcoming?id=${program_id}`
                        : ``
                    )}
                    _focus={{ boxShadow: 'none' }}
                    _hover={{ textDecoration: 'none' }}
                    prefetch
                  >
                    {title}
                  </Link>
                ) : (
                  title
                )}
              </Heading>
            </Box>
            <Box pt="8px">
              <Text
                fontFamily="var(--FedraSansStd-book)"
                fontSize={{ base: '1rem', lg: '1.125rem' }}
                fontWeight="400"
                lineHeight={{ base: '1.563rem', md: '1.75rem' }}
                color={'#FEFAF6'}
                mb="8px"
              >
                {displayDate}
              </Text>
              <Text
                fontFamily="var(--FedraSansStd-medium)"
                fontSize={{ base: '1.125rem', '2xl': '1.25rem' }}
                fontWeight="500"
                lineHeight={{
                  base: '1.563rem',
                  md: '1.75rem',
                }}
                color={'#FEFAF6'}
                mt={{ base: '10px', md: '0' }}
                pb="16px"
              >
                {samskriti.currency || currency} {samskriti.amount || amount}{' '}
              </Text>
            </Box>
          </Box>
          <Link
            w={'100%'}
            display="block"
            p="0.75rem 2rem"
            gap="1rem"
            cursor="pointer"
            textAlign={'center'}
            bgColor="#22211B"
            fontFamily="var(--FedraSansStd-medium)"
            fontSize="1rem"
            fontWeight="500"
            lineHeight="1.6rem"
            color={!isProductions ? '#EDA807' : '#F18128'}
            textTransform="uppercase"
            border="none"
            _hover={{
              backgroundColor: !isProductions ? '#EDA807' : '#F18128',
              color: '#010509',
            }}
            _focus={{
              border: 'none',
              backgroundColor: !isProductions ? '#EDA807' : '#F18128',
              outline: 'none',
              color: '#010509',
            }}
            href={refineUrlDomain(registerUrl)}
            target={isOpenInNewTab(registerUrl) ? '_blank' : ''}
            prefetch
          >
            {button_label !== '' ? button_label : 'REGISTER NOW'}
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default WorkshopsCard;
